import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalFooter, ModalBody, Alert, Label, Button, ModalHeader } from 'reactstrap';
import * as history from 'history';
import entityComposition from '../common/classes/entityComposition';
import SinglePicker from '../common/controls/singlePicker';
import * as ajax from '../../actions/ajax';
import Loader from '../common/loader';
import store from '../../reducers/storeRegistration';
import loginActions from '../../actions/loginActionCreators';
const hashHistory = history.createHashHistory();
const IssueExportModal = _ref => {
  let _ref$reportTypeID = _ref.reportTypeID,
    reportTypeID = _ref$reportTypeID === void 0 ? undefined : _ref$reportTypeID,
    _ref$reportIssueBulkE = _ref.reportIssueBulkExport,
    reportsList = _ref$reportIssueBulkE.list,
    reportIndexQuery = _ref$reportIssueBulkE.indexQuery,
    reportActionsServerFilter = _ref.reportIssueBulkExportActions.serverFilter,
    _ref$jobIssueBulkExpo = _ref.jobIssueBulkExport,
    jobsList = _ref$jobIssueBulkExpo.list,
    jobIndexQuery = _ref$jobIssueBulkExpo.indexQuery,
    jobServerFilter = _ref.jobIssueBulkExportActions.serverFilter,
    _ref$createNewReport = _ref.createNewReport,
    createNewReport = _ref$createNewReport === void 0 ? false : _ref$createNewReport,
    _ref$selected = _ref.selected,
    selectedIssues = _ref$selected === void 0 ? [] : _ref$selected,
    _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
    toggle = _ref.toggle;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    selectedReport = _useState2[0],
    setSelectedReport = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedJob = _useState4[0],
    setSelectedJob = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    exportingIssues = _useState6[0],
    setExportingIssues = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    exportingErrorMessage = _useState8[0],
    setExportingErrorMessage = _useState8[1];

  // load job picker options (if open and creating new report)
  useEffect(() => {
    if (isOpen && createNewReport) jobServerFilter({}, jobIndexQuery);
  }, [createNewReport, jobServerFilter, jobIndexQuery, isOpen]);

  // load report picker options (if open and not creating new report)
  useEffect(() => {
    if (isOpen && !createNewReport) reportActionsServerFilter({
      reportTypeIdentifier: 'issueExportReport'
    }, reportIndexQuery);
  }, [reportActionsServerFilter, reportIndexQuery, createNewReport, isOpen]);
  const dismissModal = () => {
    if (createNewReport) setSelectedJob(null);
    if (!createNewReport) setSelectedReport(null);
    setExportingErrorMessage(false);
    setExportingIssues(false);
    toggle();
  };
  const buttonDisabled = createNewReport ? selectedJob === null : selectedReport === null;
  const handleIssueExport = async () => {
    // setup the targets based on selections
    const target = createNewReport ? {
      jobID: selectedJob
    } : {
      reportID: selectedReport
    };
    target.reportTypeID = reportTypeID;

    // start the loading indicator and kick off the request
    setExportingIssues(true);
    try {
      const _await$ajax$post = await ajax.post('/issues/export', {
          target,
          issues: selectedIssues
        }),
        _await$ajax$post$data = _await$ajax$post.data,
        reportID = _await$ajax$post$data.reportID,
        error = _await$ajax$post$data.error;
      setExportingIssues(false);

      // set error to state for display
      if (error) setExportingErrorMessage(error);

      // redirect to the reportID of choice
      if (reportID) {
        dismissModal();
        hashHistory.push("/reports/".concat(reportID));
      }
    } catch (e) {
      var _e$response;
      // If token expired (401), refresh and retry once
      if (((_e$response = e.response) === null || _e$response === void 0 ? void 0 : _e$response.status) === 401) {
        try {
          await store.dispatch(loginActions.serverToken());
          const _await$ajax$post2 = await ajax.post('/issues/export', {
              target,
              issues: selectedIssues
            }),
            _await$ajax$post2$dat = _await$ajax$post2.data,
            reportID = _await$ajax$post2$dat.reportID,
            error = _await$ajax$post2$dat.error;
          setExportingIssues(false);

          // set error to state for display
          if (error) setExportingErrorMessage(error);

          // redirect to the reportID of choice
          if (reportID) {
            dismissModal();
            hashHistory.push("/reports/".concat(reportID));
          }
          return;
        } catch (retryError) {
          // Retry failed, fall through to error handling
        }
      }
      setExportingIssues(false);
      setExportingErrorMessage('Failed to export issues. Please try again.');
    }
  };
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    toggle: () => dismissModal()
  }, /*#__PURE__*/React.createElement(ModalHeader, null, "Export issues"), /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(Label, null, "Select a", createNewReport ? ' Job to create the new Issue Export Report under' : 'n existing Issue Export Report'), createNewReport ? /*#__PURE__*/React.createElement(SinglePicker, {
    disabled: exportingIssues,
    id: "add-select-job",
    className: "t-s-job",
    options: jobsList.map(job => {
      const jobOption = {
        name: job.jobNumber,
        id: job.id
      };
      if (job.name) jobOption.subtitle = job.name;
      return jobOption;
    }),
    onChange: job => {
      setSelectedJob(job ? jobsList.find(r => r.id === job.id) : null);
    },
    placeholder: "Required",
    value: selectedJob
  }) : /*#__PURE__*/React.createElement(SinglePicker, {
    disabled: exportingIssues,
    id: "add-select-report",
    className: "t-s-report",
    options: reportsList.map(report => ({
      name: "".concat(report.reportTypeName, " ").concat(report.reportNumber),
      id: report.id,
      subtitle: "".concat(report.jobNumber, " - ").concat(report.appCreated)
    })),
    onChange: report => {
      setSelectedReport(report ? reportsList.find(r => r.id === report.id) : null);
    },
    placeholder: "Required",
    value: selectedReport
  }), /*#__PURE__*/React.createElement(Loader, {
    loaded: !exportingIssues
  }), /*#__PURE__*/React.createElement(Alert, {
    color: "danger",
    isOpen: exportingErrorMessage
  }, exportingErrorMessage)), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    onClick: dismissModal,
    className: "t-iem-cancel",
    disabled: exportingIssues
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    disabled: buttonDisabled || exportingIssues,
    className: "t-iem-assign",
    onClick: handleIssueExport
  }, createNewReport ? 'Create new report' : 'Add issues to existing report')));
};
IssueExportModal.propTypes = {
  createNewReport: PropTypes.bool,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  reportIssueBulkExport: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({})
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  reportIssueBulkExportActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  jobIssueBulkExport: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({})
    }),
    list: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  jobIssueBulkExportActions: PropTypes.shape({
    serverFilter: PropTypes.func
  }).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string),
  reportTypeID: PropTypes.string
};
export { IssueExportModal as TestIssueExportModal };
export default entityComposition(IssueExportModal, ['reportIssueBulkExport', 'jobIssueBulkExport']);